import { useEffect } from "react";
import { Navigate, Outlet } from "react-router-dom";
import styled from "styled-components";

import { VersionTag } from "../../core/components/VersionTag";
import { getLoggedIn } from "../../core/store/appSlice";
import {
  getIsReleaseSession,
  getLeftForOtherPerson,
  getPracticeFormId,
  getPracticePatientId,
  getWebAppChoices,
} from "../../core/store/currentFormSlice";
import { useMainStore } from "../../core/store/mainStore";
import { reloadIfDeferred } from "../../core/utils/useRefreshOutdatedApp";
import { Header } from "./components/Header";
import { useHandleMissingData } from "./utils/useHandleMissingData";
import { useScrollToTop } from "./utils/useScrollToTop";
import {
  useEndReleaseSessionWithoutRecord,
  useEndSessionOnForeignLink,
  useWebAppSessionTimeout,
} from "./utils/webAppSession";

export const InAppLayout = () => {
  const isLoggedIn = useMainStore(getLoggedIn);
  useScrollToTop(false);
  useHandleMissingData();
  useWebAppSessionTimeout();
  const linkChanged = useEndSessionOnForeignLink();
  useEndReleaseSessionWithoutRecord();
  useEffect(() => reloadIfDeferred(), []);
  const practiceFormId = useMainStore(getPracticeFormId);
  const practicePatientId = useMainStore(getPracticePatientId);
  const webAppChoices = useMainStore(getWebAppChoices);
  const leftForOtherPerson = useMainStore(getLeftForOtherPerson);
  const viaRelease = useMainStore(getIsReleaseSession);
  const pathname = window.location.pathname;

  // Signed in is not enough: in the chooser the session has no record yet.
  if (!isLoggedIn || !practicePatientId) {
    // A release session (PF-6133) has no chooser and no SMS login: useEndReleaseSessionWithoutRecord ends it on the
    // release end screen, so it must not reach the redirects below.
    if (viaRelease) {
      return null;
    }
    // A tab that left its record goes to the chooser, or after „Ja, andere Person" to the identity step. This is the
    // only navigation of that way out, so no other redirect can race it.
    if (isLoggedIn && webAppChoices?.length) {
      return <Navigate to={leftForOtherPerson ? "/welcome/other-person" : "/welcome/select"} replace />;
    }
    // Not `invite`: that route resets the patient's link state, and this
    // redirect carries no link parameters it could be restored from.
    return <Navigate to={"/welcome"} replace />;
  } else if (linkChanged) {
    return null;
  } else if (practiceFormId && !pathname.includes("form")) {
    return <Navigate to={"form"} replace />;
  }
  return (
    <>
      <Header />
      <Container>
        <ScrollContainer className="scroll-ancestor">
          <OutletContainer>
            <Outlet />
            <VersionTag />
          </OutletContainer>
        </ScrollContainer>
      </Container>
    </>
  );
};

const OutletContainer = styled.div`
  max-width: 600px;
  width: 100%;
  height: 100%;
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
  height: 100%;
  overflow: hidden;
`;

const ScrollContainer = styled.div`
  overflow-y: auto;
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
`;
